import { styled } from '@mui/material/styles';
import {
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  MenuItem,
  Drawer,
  List,
  ListItemButton,
  Chip,
  IconButton,
} from '@mui/material';
import { FORD_COLORS, FORD_TYPOGRAPHY, FORD_SPACING, FORD_RADIUS, FORD_SHADOWS } from '../../theme/fordTheme.js';

// Common constants to reduce duplication
const COMMON_COLORS = {
  primary: '#4f7cff',
  primaryHover: '#3d63cc',
  primaryLight: '#233058',
  border: '#233058',
  hoverBackground: 'rgba(79, 124, 255, 0.1)',
  contextBackground: 'rgba(79, 124, 255, 0.08)',
};

const COMMON_SIZES = {
  fontSize: '0.875rem',
  borderRadius: '8px',
  padding: '8px 12px',
};

// Common style objects
const commonHoverStyles = {
  '&:hover': {
    backgroundColor: COMMON_COLORS.hoverBackground,
    borderColor: COMMON_COLORS.primary,
  },
};

const commonContextStyles = {
  marginBottom: '8px',
  padding: '10px 16px', // Adjusted padding for 38px alignment
  backgroundColor: COMMON_COLORS.contextBackground,
  borderRadius: '6px',
};

// Main container - Ford theme
export const DashboardContainer = styled(Box)(({ theme }) => ({
  height: '100vh', // Fixed height to enable scrolling
  maxHeight: '100vh', // Prevent container from growing beyond viewport
  backgroundColor: FORD_COLORS.backGroundColor,

  color: FORD_COLORS.textLight,
  fontFamily: FORD_TYPOGRAPHY.newfont,
  padding: '28px',
  // border: `1px solid ${FORD_COLORS.primaryLight}`,
  // borderRadius: FORD_RADIUS.sm,
  width: '100%',
  // boxSizing: 'border-box',
  overflowY: 'scroll', // Force vertical scrollbar to always show
  overflowX: 'hidden', // Hide horizontal scroll
  // Firefox scrollbar styling
  scrollbarWidth: 'thin', // Firefox - show thin scrollbar
  scrollbarColor: `${COMMON_COLORS.border} #f0f0f0`, // Firefox scrollbar colors
  // Webkit scrollbar styling (Chrome, Safari, Edge)
  border: 'none',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
  '&::-webkit-scrollbar': {
    width: '8px', // Chrome, Safari, Edge - show scrollbar
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f0f0f0',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: COMMON_COLORS.border,
    borderRadius: '4px',
    '&:hover': {
      background: '#1a2442',
    },
  },
}));

// Outer container for content
export const DashboardOuterContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '100vw',
  margin: '0 auto',
  padding: '0',
  // boxSizing: 'border-box',
  // overflow: 'hidden',
  // boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)'
}));

// Page header styling - matching OutagePageHeader
export const DashboardPageHeader = styled(Box)(({ theme }) => ({
  marginBottom: '10px',
  '& h2': {
    fontSize: FORD_TYPOGRAPHY.h4,
    fontWeight: FORD_TYPOGRAPHY.extraBold,
    fontFamily: FORD_TYPOGRAPHY.fallbackFont,
    color: 'rgb(19, 34, 52)',
    letterSpacing: FORD_TYPOGRAPHY.letterSpacingWide
  },
  '& .subtitle': {
    color: 'rgb(94, 110, 126)',
    fontSize: FORD_TYPOGRAPHY.body2,
    marginTop: '4px',
  },
}));

// Sidebar toggle button
export const DashboardSidebarToggle = styled(IconButton)(({ theme }) => ({
  backgroundColor: FORD_COLORS.backgroundford,
  border: `1px solid ${COMMON_COLORS.border}`,
  borderRadius: '8px',
  padding: '6px',
  color: COMMON_COLORS.primary,
  minWidth: '38px',
  width: '38px',
  height: '38px',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    backgroundColor: COMMON_COLORS.hoverBackground,
    borderColor: COMMON_COLORS.primary,
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '18px',
  },
}));

// Toggle button and search context row container
export const DashboardToggleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginBottom: '16px',
  padding: '0px',
  width: '100%',
  minHeight: '38px',
  // Ensure search context in this row aligns properly
  '& > *:last-child': {
    flex: 1,
    marginBottom: 0,
  },
}));

// Panel styling - Ford theme base component
export const DashboardPanel = styled(Paper)(({ theme }) => ({
  backgroundColor: FORD_COLORS.backgroundford,
  // border: `1px solid ${FORD_COLORS.primaryLight}`,
  borderRadius: FORD_RADIUS.lg,
  padding: FORD_SPACING.lg,
  color: FORD_COLORS.textLight,
  fontFamily: FORD_TYPOGRAPHY.newfont,
  // boxShadow: FORD_SHADOWS.sm,
  marginBottom: FORD_SPACING.md,
   border: 'none',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)'
}));

// Layout container for left sidebar and main content - Ford theme
export const DashboardLayout = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: FORD_SPACING.sm,
  alignItems: 'flex-start',
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: FORD_SPACING.sm,
  },
}));

// Left sidebar for hierarchy with collapse functionality
export const DashboardSidebar = styled(Box)(({ theme, isOpen }) => ({
  width: isOpen ? '230px' : '0px',
  minWidth: isOpen ? '230px' : '0px',
  maxWidth: isOpen ? '230px' : '0px',
  flexShrink: 0,
  overflow: 'hidden',
  transition: 'all 0.3s ease-in-out',
  opacity: isOpen ? 1 : 0,
  visibility: isOpen ? 'visible' : 'hidden',
  [theme.breakpoints.down('md')]: {
    width: isOpen ? '100%' : '0px',
    minWidth: isOpen ? '100%' : '0px',
    maxWidth: isOpen ? '100%' : '0px',
    height: isOpen ? '100%' : '0px',
  },
}));

// Main content area with responsive width based on sidebar state
export const DashboardMainContent = styled(Box)(({ theme, isSidebarOpen }) => ({
  flex: 1,
  minWidth: 0,
  maxWidth: isSidebarOpen ? 'calc(100% - 230px - 8px)' : '100%',
  width: isSidebarOpen ? 'calc(100% - 230px - 8px)' : '100%',
  overflow: 'visible',
  transition: 'all 0.3s ease-in-out',
  // backgroundColor: FORD_COLORS.backgroundford,
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
    width: '100%',
  },
}));

// Hierarchy panel - Ford theme
export const DashboardHierarchyPanel = styled(DashboardPanel)(({ theme }) => ({
  padding: FORD_SPACING.md,
  '& h3': {
    fontSize: FORD_TYPOGRAPHY.body1,
    // fontWeight: FORD_TYPOGRAPHY.bold,
    fontFamily: FORD_TYPOGRAPHY.newfont,
    color: FORD_COLORS.textLight,
    // margin: `0 0 ${FORD_SPACING.md} 0`,
    letterSpacing: FORD_TYPOGRAPHY.letterSpacingNormal
  },
}));

// Tree root button - Ford theme
export const DashboardTreeRoot = styled(Button)(({ theme }) => ({
  width: '100%',
  justifyContent: 'flex-start',
  textTransform: 'none',
  fontFamily: FORD_TYPOGRAPHY.newfont,
  fontSize: FORD_TYPOGRAPHY.body2,
  fontWeight: FORD_TYPOGRAPHY.medium,
  padding: `${FORD_SPACING.xs} ${FORD_SPACING.xs}`,
  transition: 'all 0.2s ease-in-out',
  color: 'rgb(19, 34, 52)',
  border: 'none',
  '&:hover': {
    backgroundColor: `${FORD_COLORS.secondary}20`,
    borderColor: FORD_COLORS.secondary,
    transform: 'translateY(-1px)',
  },
  '&.active': {
    boxShadow: FORD_SHADOWS.sm,
  },
}));

// Tree list
export const DashboardTreeList = styled(List)(({ theme }) => ({
  padding: 0,
  '& .MuiListItem-root': {
    padding: 0,
  },
}));

// Tree site item - Ford theme
export const DashboardTreeSite = styled(ListItemButton)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: 'rgb(19, 34, 52)',
  fontFamily: FORD_TYPOGRAPHY.newfont,
  fontSize: FORD_TYPOGRAPHY.body2,
  fontWeight: FORD_TYPOGRAPHY.medium,
  // borderRadius: FORD_RADIUS.sm,
  marginBottom: FORD_SPACING.xs,
  padding: `${FORD_SPACING.xs} ${FORD_SPACING.xs}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: `${FORD_COLORS.primaryLight}20`,
    transform: 'translateX(4px)',
  },
  '&.active': {
    backgroundColor: `${FORD_COLORS.secondary}20`,
    color: FORD_COLORS.secondary,
    fontWeight: FORD_TYPOGRAPHY.semibold,
  },
}));

// Tree location item - Ford theme
export const DashboardTreeLocation = styled(ListItemButton)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: 'rgb(94, 110, 126)',
  fontFamily: FORD_TYPOGRAPHY.newfont,
  fontSize: FORD_TYPOGRAPHY.body2,
  fontWeight: FORD_TYPOGRAPHY.regular,
  // borderRadius: FORD_RADIUS.sm,
  marginBottom: FORD_SPACING.xs,
  marginLeft: FORD_SPACING.md,
  padding: `${FORD_SPACING.xs} ${FORD_SPACING.md}`,
  paddingLeft: '0px',
  paddingRight: '0px',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: `${FORD_COLORS.primaryLight}15`,
    color: FORD_COLORS.textLight,
    transform: 'translateX(4px)',
  },
  '&.active': {
    backgroundColor: `${FORD_COLORS.secondary}15`,
    color: FORD_COLORS.secondary,
    fontWeight: FORD_TYPOGRAPHY.medium,
  },
}));

// Count badge - Ford theme
export const DashboardCountBadge = styled('span')(({ theme }) => ({
  backgroundColor: `${FORD_COLORS.secondary}30`,
  color: FORD_COLORS.secondary,
  fontFamily: FORD_TYPOGRAPHY.fallbackFont,
  fontSize: FORD_TYPOGRAPHY.caption,
  fontWeight: FORD_TYPOGRAPHY.medium,
  padding: `${FORD_SPACING.xs} ${FORD_SPACING.sm}`,
  borderRadius: FORD_RADIUS.full,
  marginLeft: 'auto',
  border: `1px solid ${FORD_COLORS.secondary}50`,
}));

// Filters panel
export const DashboardFiltersPanel = styled(DashboardPanel)(({ theme }) => ({
  marginBottom: '10px',
  padding: FORD_SPACING.md,
  display: 'none'
}));

// Base context display component
const BaseContextDisplay = styled(Box)(({ theme }) => ({
  ...commonContextStyles,
}));

// Search context display
export const DashboardSearchContext = styled(BaseContextDisplay)(({ theme }) => ({
  borderLeft: `3px solid ${COMMON_COLORS.primaryLight}`,
  boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
  width: '100%', // Ensure full width to prevent text truncation
  overflow: 'visible', // Allow text to display fully
  whiteSpace: 'normal', // Allow text wrapping if needed
  background: FORD_COLORS.primary,
  marginBottom: '0px', // Remove margin to align with toggle button
  minHeight: '38px', // Match toggle button height
  display: 'flex',
  alignItems: 'center',
}));


// Offline device context display
export const DashboardOfflineDeviceContext = styled(BaseContextDisplay)(({ theme }) => ({
  borderLeft: '3px solid #f44336',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
  background: FORD_COLORS.primary
}));


// Search context text
export const DashboardSearchContextText = styled(Typography)(({ theme }) => ({
  color: COMMON_COLORS.primaryLight,
  fontWeight: 500,
  fontSize: COMMON_SIZES.fontSize,
  background: FORD_COLORS.primary,
  fontFamily: FORD_TYPOGRAPHY.newfont,
  lineHeight: '1.4',
  margin: 0, // Remove any default margins
}));

// Filters row
export const DashboardFiltersRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  '& > *': {
    minWidth: '150px',
  },
}));

// Left section for filters
export const DashboardFiltersLeft = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
  flexWrap: 'wrap',
}));

// Right section for actions
export const DashboardFiltersRight = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
  flexWrap: 'wrap',
}));

// Filter label styling - matching OutageFilterLabel
export const DashboardFilterLabel = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: FORD_TYPOGRAPHY.bold,
  color: 'rgb(94, 110, 126)',
  marginBottom: '4px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  fontFamily: FORD_TYPOGRAPHY.newfont,
}));

// Select field styling - matching OutageSelect
export const DashboardSelect = styled(FormControl)(({ theme }) => ({
  minWidth: '150px',
  '& .MuiOutlinedInput-root': {
    backgroundColor: `${FORD_COLORS.backgroundford} !important`,
    color: `${FORD_COLORS.textLight} !important`,
    borderRadius: '8px',
    '& fieldset': {
      borderColor: 'rgb(209, 213, 219) !important',
    },
    '&:hover fieldset': {
      borderColor: `${COMMON_COLORS.primary} !important`,
    },
    '&.Mui-focused fieldset': {
      borderColor: `${COMMON_COLORS.primary} !important`,
    },
  },
  '& .MuiSelect-select': {
    backgroundColor: `${FORD_COLORS.backgroundford} !important`,
    paddingTop: '10px',
    paddingBottom: '8px',
    fontSize: '14px',
    fontFamily: FORD_TYPOGRAPHY.newfont
  },
  '& .MuiSelect-icon': {
    color: `${FORD_COLORS.textLight} !important`,
  },
  '& .MuiInputBase-root': {
    backgroundColor: `${FORD_COLORS.backgroundford} !important`,
    color: `${FORD_COLORS.textLight} !important`
  },
}));

// MenuItem styling
export const DashboardMenuItem = styled(MenuItem)(({ theme }) => ({
  // backgroundColor: '#0c142e !important',
  backgroundColor: `${FORD_COLORS.backgroundford} !important`,
  color: `${FORD_COLORS.textLight} !important`,
  '&:hover': {
    backgroundColor: `${FORD_COLORS.primaryLight} !important`,
  },
  '&.Mui-selected': {
    backgroundColor: `${FORD_COLORS.secondary} !important`,
    color: `${FORD_COLORS.textColorWhite} !important`,
    '&:hover': {
      backgroundColor: `${FORD_COLORS.primaryLight} !important`,
      // color: `${FORD_COLORS.textColorWhite} !important`,
    },
  },
}));

// Base button styling - shared between primary and refresh buttons
const basePrimaryButtonStyles = {
  backgroundColor: '#066fef',
  color: '#ffffff',
  textTransform: 'none',
  borderRadius: COMMON_SIZES.borderRadius,
  padding: COMMON_SIZES.padding,
  fontWeight: '500',
  '&:hover': {
    backgroundColor: '#0554c7',
  },
  '&:disabled': {
    backgroundColor: COMMON_COLORS.border,
    color: '#9aa3c7',
  },
};

// Base secondary button styling
const baseSecondaryButtonStyles = {
  backgroundColor: 'transparent',
  color: '#e7ecff',
  textTransform: 'none',
  borderRadius: COMMON_SIZES.borderRadius,
  padding: COMMON_SIZES.padding,
  fontWeight: '500',
  border: `1px solid ${COMMON_COLORS.border}`,
  ...commonHoverStyles,
  '&:disabled': {
    backgroundColor: 'transparent',
    color: '#9aa3c7',
    borderColor: COMMON_COLORS.border,
  },
};

// Primary button styling
export const DashboardPrimaryButton = styled(Button)(({ theme }) => ({
  ...basePrimaryButtonStyles,
}));

// Secondary button styling
export const DashboardSecondaryButton = styled(Button)(({ theme }) => ({
  ...baseSecondaryButtonStyles,
}));

// KPIs container
export const DashboardKpisContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '16px',
  // marginBottom: '10px',
  flexWrap: 'wrap',
  position: 'relative',
  height: '115px',
  '&.loading-overlay': {
    opacity: 0.6,
    pointerEvents: 'none',
  },
}));

// Loading overlay for KPIs
export const DashboardLoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  color: COMMON_COLORS.primary,
  fontSize: '0.9rem',
  zIndex: 1,
}));

// Table container
export const DashboardTableContainer = styled(DashboardPanel)(({ theme }) => ({
  padding: '4px',
  overflow: 'hidden',
}));

// Table actions header
export const DashboardTableActions = styled(Box)(({ theme }) => ({
  padding: '16px 20px',
  borderBottom: `1px solid ${COMMON_COLORS.border}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  color: FORD_COLORS.textMuted
}));

export const DashboardTableWrapper = styled(Box)(({ theme }) => ({
  overflowX: 'auto',
  overflowY: 'auto',
  position: 'relative', // Create stacking context
  // border: `1px solid ${COMMON_COLORS.border}`,
  // borderRadius: '8px',
  // Show horizontal scrollbar for better UX
  scrollbarWidth: 'thin', // Firefox
  '&::-webkit-scrollbar': {
    height: '8px', // Chrome, Safari, Edge - horizontal scrollbar height
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f1f3f4',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#c1c1c1',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: '#a1a1a1',
    },
  },
  '& table': {
    width: '1200px', // Fixed width to enable horizontal scroll
    minWidth: '100%',
    borderCollapse: 'separate', // Changed from collapse to separate for better sticky behavior
    borderSpacing: 0,
    textAlign: 'left',
    fontSize: '12px',
    fontFamily: FORD_TYPOGRAPHY.newfont,
    '& thead': {
      position: 'sticky',
      top: 0,
      zIndex: 10, // Higher z-index to ensure it stays above tbody
     padding: '8px 10px',
      height: '42px',
    },
    '& th': {
      // backgroundColor: 'rgba(30,45,90,0.8)',
      backdropFilter: 'blur(8px)',
      // color: '#e7ecff',
      // fontWeight: '600',
      // padding: '10px',
      // textAlign: 'left',
      // borderBottom: `2px solid ${COMMON_COLORS.border}`,
      // fontSize: COMMON_SIZES.fontSize,
      position: 'sticky',
      top: 0,
      zIndex: 11,
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
      borderRadius: 0,
      backgroundColor: 'rgb(10, 37, 64)', // Dark navy blue background
        color: '#ffffff', // White text
        fontWeight: 700,
        fontSize: '12px', // Smaller font size
        padding: '8px 10px',// Reduced padding
        textAlign: 'left',
        textTransform: 'uppercase', // Capital letters
        letterSpacing: '0.5px', // Better readability
        borderBottom: 'none',

        // Column-specific widths for Dashboard table
      // '&:nth-of-type(1)': { width: '180px', minWidth: '150px' }, // Device Name
      // '&:nth-of-type(2)': { width: '200px', minWidth: '180px' }, // Device Mac ID
      // '&:nth-of-type(3)': { width: '150px', minWidth: '120px' }, // Location
      // '&:nth-of-type(4)': { width: '100px', minWidth: '80px' }, // Status
      // '&:nth-of-type(5)': { width: '140px', minWidth: '120px' }, // Smart Plug Location
      // '&:nth-of-type(6)': { width: '180px', minWidth: '150px' }, // Modem ID
      // '&:nth-of-type(7)': { width: '200px', minWidth: '180px' }, // Critical Devices
      // '&:nth-of-type(8)': { width: '150px', minWidth: '130px' }, // Last Heartbeat
    },
    '& tbody': {
      position: 'relative',
      zIndex: 1, // Lower z-index than header
    },
    '& td': {
      // padding: '10px',
      borderBottom: '1px solid lightGray',
      color: FORD_COLORS.textMuted,
      fontSize: '12px',
      backgroundColor: FORD_COLORS.backgroundford,
       height: '42px',

      // Column-specific widths matching header
      // '&:nth-of-type(1)': { width: '180px', minWidth: '150px' }, // Device Name
      // '&:nth-of-type(2)': { width: '200px', minWidth: '180px' }, // Device Mac ID
      // '&:nth-of-type(3)': { width: '150px', minWidth: '120px' }, // Location
      // '&:nth-of-type(4)': { width: '100px', minWidth: '80px' }, // Status
      // '&:nth-of-type(5)': { width: '140px', minWidth: '120px' }, // Smart Plug Location
      // '&:nth-of-type(6)': { width: '180px', minWidth: '150px' }, // Modem ID
      // '&:nth-of-type(7)': { width: '200px', minWidth: '180px' }, // Critical Devices
      // '&:nth-of-type(8)': { width: '150px', minWidth: '130px' }, // Last Heartbeat
    },
    '& tr': {
        fontSize: '12px', // Smaller font size
        padding: '4px', // Reduced padding
        borderTop: '1px solid rgb(228, 234, 241)'
    },
    '& tr:hover td': {
      backgroundColor: COMMON_COLORS.hoverBackground,
    },
  },
}));


// Device MAC link styling with underline
export const DashboardDeviceMacLink = styled('span')(({ theme }) => ({
  color: COMMON_COLORS.primary,
  fontSize: COMMON_SIZES.fontSize,
  textDecoration: 'underline',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  wordBreak: 'break-all',
  '&:hover': {
    color: COMMON_COLORS.primaryHover,
    textDecoration: 'underline',
    textDecorationThickness: '2px',
  },
}));

// Status chip styling
export const DashboardStatusChip = styled(Chip)(({ theme, chiptype }) => {
  const getChipStyles = () => {
    switch (chiptype) {
      case 'chip-ok':
        return {
          backgroundColor: 'rgba(34, 197, 94, 0.2)',
          color: '#22c55e',
          border: '1px solid rgba(34, 197, 94, 0.3)',
        };
      case 'chip-warn':
        return {
          backgroundColor: 'rgba(251, 191, 36, 0.2)',
          color: '#fbbf24',
          border: '1px solid rgba(251, 191, 36, 0.3)',
        };
      case 'chip-error':
        return {
          backgroundColor: 'rgba(239, 68, 68, 0.2)',
          color: '#ef4444',
          border: '1px solid rgba(239, 68, 68, 0.3)',
        };
      default:
        return {
          backgroundColor: 'rgba(156, 163, 199, 0.2)',
          color: '#9aa3c7',
          border: '1px solid rgba(156, 163, 199, 0.3)',
        };
    }
  };

  return {
    ...getChipStyles(),
    fontSize: '0.75rem',
    height: '24px',
    borderRadius: '12px',
    fontWeight: '500',
  };
});

// Socket chip styling
export const DashboardSocketChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(79, 124, 255, 0.2)',
  color: COMMON_COLORS.primary,
  border: '1px solid rgba(79, 124, 255, 0.3)',
  fontSize: '0.75rem',
  height: '24px',
  borderRadius: '12px',
  fontWeight: '500',
}));

// Loading message
export const DashboardLoadingMessage = styled(Box)(({ theme }) => ({
  // color: '#9aa3c7',
  textAlign: 'center',
  padding: '40px',
  fontSize: '1rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

// Error message
export const DashboardErrorMessage = styled(Typography)(({ theme }) => ({
  color: '#ef4444',
  textAlign: 'center',
  padding: '40px',
  fontSize: '1rem',
}));

// No data message
export const DashboardNoDataMessage = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: '40px',
  color: FORD_COLORS.textMuted,
  '& .title': {
    fontSize: '16px',
    marginBottom: '8px',
  },
  '& .subtitle': {
    fontSize: '14px',
  },
}));

// Drawer styling
export const DashboardDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    backgroundColor: '#0b1020',
    color: '#e7ecff',
    width: '600px',
    maxWidth: '90vw',
    border: 'none',
    borderLeft: `1px solid ${COMMON_COLORS.border}`,
  },
  // Ensure proper scroll management
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
}));

// Drawer header
export const DashboardDrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px',
  borderBottom: `1px solid ${COMMON_COLORS.border}`,
  '& h3': {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#e7ecff',
    margin: 0,
  },
}));

// Drawer content
export const DashboardDrawerContent = styled(Box)(({ theme }) => ({
  padding: '20px',
  height: 'calc(100vh - 80px)',
  overflowY: 'auto',
}));

// Meta information section
export const DashboardMetaSection = styled(Box)(({ theme }) => ({
  marginBottom: '24px',
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '2px',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    // gap: '8px',
  },
  '& > div': {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '4px', // Reduced from 8px to 4px
    '& b': {
      minWidth: '120px',
      color: '#e7ecff',
      fontWeight: '600',
    },
  },
}));

// Refresh info
export const DashboardRefreshInfo = styled(Typography)(({ theme }) => ({
  color: FORD_COLORS.textLight,
  fontSize: '0.8rem',
  fontStyle: 'italic',
}));

// Spinner
export const DashboardSpinner = styled(Box)(({ theme }) => ({
  width: '20px',
  height: '20px',
  border: `2px solid ${COMMON_COLORS.border}`,
  borderTop: `2px solid ${COMMON_COLORS.primary}`,
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
}));

// Pagination container
export const DashboardPagination = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px 10px',
  borderTop: `1px solid ${COMMON_COLORS.border}`,
  backgroundColor: FORD_COLORS.backgroundford,
  fontSize: '12px'
}));

// Pagination controls
export const DashboardPaginationControls = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
}));

// Pagination button
export const DashboardPaginationButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: FORD_COLORS.textMuted,
  textTransform: 'none',
  borderRadius: '6px',
  padding: '6px 12px',
  minWidth: '36px',
  fontSize: '12px',
  border: `1px solid ${COMMON_COLORS.border}`,
  ...commonHoverStyles,
  '&:disabled': {
    backgroundColor: 'transparent',
    color: '#9aa3c7',
    borderColor: COMMON_COLORS.border,
    opacity: 0.5,
  },
  '&.active': {
    backgroundColor: COMMON_COLORS.primary,
    color: '#ffffff',
    borderColor: COMMON_COLORS.primary,
    '&:hover': {
      backgroundColor: COMMON_COLORS.primaryHover,
    },
  },
}));

// Pagination info
export const DashboardPaginationInfo = styled(Typography)(({ theme }) => ({
  color: FORD_COLORS.textMuted,
  fontSize: COMMON_SIZES.fontSize,
}));

// Table actions container - MUI version
export const DashboardTableActionsMUI = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px 16px',
  borderBottom: `1px solid ${FORD_COLORS.primaryLight}`,
  backgroundColor: FORD_COLORS.backgroundford,
}));

// Table actions right section
export const DashboardTableActionsRight = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: FORD_SPACING.md,
}));

// Refresh button - MUI styled
export const DashboardRefreshButton = styled(Button)(({ theme }) => ({
  ...basePrimaryButtonStyles,
  padding: '2px 9px',
}));

// Blinking green dot animation
const blinkAnimation = `
  @keyframes blink {
    0%, 50% {
      opacity: 1;
    }
    51%, 100% {
      opacity: 0.3;
    }
  }
`;

// Blinking green dot component
export const BlinkingGreenDot = styled('span')`
  ${blinkAnimation}
  display: inline-block;
  width: 8px;
  height: 8px;
  background-color: #4CAF50;
  border-radius: 50%;
  margin-right: 6px;
  animation: blink 1.5s infinite;
  flex-shrink: 0;
`;

// Last refreshed chip - MUI styled
export const DashboardLastRefreshedChip = styled(Chip)(({ theme }) => ({
  backgroundColor: `${FORD_COLORS.primaryLight}20`,
  borderColor: FORD_COLORS.primaryLight,
  color: FORD_COLORS.textMuted,
  fontSize: FORD_TYPOGRAPHY.caption,
  height: '28px',
  borderRadius: FORD_RADIUS.sm,
  fontWeight: FORD_TYPOGRAPHY.regular,
  '& .MuiChip-label': {
    padding: `0 ${FORD_SPACING.sm}`,
    display: 'flex',
    alignItems: 'center',
  },
}));

// Generic display component with copy functionality
export const DashboardCopyableDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '1px', // Further reduced for tighter spacing between text and copy icon
  '& .copy-icon': {
    fontSize: '16px',
    color: '#9aa3c7',
    cursor: 'pointer',
    opacity: 0.7,
    transition: 'all 0.2s ease',
    marginRight: '8px', // Add margin to create space between copy icon and next column
    '&:hover': {
      color: COMMON_COLORS.primary,
      opacity: 1,
    }
  }
}));

// Monospace text for technical identifiers (MAC addresses, Modem IDs)
export const DashboardMonospaceText = styled('span')(({ theme, clickable }) => ({
  fontFamily: '"Monaco", "Menlo", "Ubuntu Mono", "Consolas", "source-code-pro", monospace',
  // fontSize: '0.8125rem',
  color: clickable ? COMMON_COLORS.primary : '#1e6fd9 !important',
  // fontWeight: 500,
  letterSpacing: '0.025em',
  cursor: clickable ? 'pointer' : 'default',
  textDecoration: clickable ? 'underline' : 'none',
  textDecorationStyle: 'solid',
  // display: 'inline-block',
  // whiteSpace: 'nowrap',
  '&:hover': clickable ? {
    color: COMMON_COLORS.primaryDark,
    textDecoration: 'underline',
  } : {}
}));

// Device Management Navigation Info
export const DashboardDeviceManagementInfo = styled(Typography)(({ theme }) => ({
  color: FORD_COLORS.textMuted,
  fontSize: '10px',
  textAlign: 'right',
  fontStyle: 'italic',
  marginTop: '20px',
  marginBottom: '4px',
  '& a, & a:visited': {
    color: COMMON_COLORS.primary,
    textDecoration: 'none',
    fontWeight: 300,
    transition: 'all 0.2s ease',
    '&:hover': {
      textDecoration: 'underline',
      color: COMMON_COLORS.primaryHover
    }
  }
}));

// Table filter buttons container
export const DashboardTableFilters = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  backgroundColor: 'transparent',
  borderRadius: '6px',
}));

// Individual filter button
export const DashboardTableFilterButton = styled(Button)(({ theme, active }) => ({
  // backgroundColor: active ? COMMON_COLORS.primary : 'lightgray',
 
    background: active ?  'rgb(237, 243, 254)' : 'rgb(255, 255, 255)',
  color: active ? 'rgb(30, 111, 217)' : 'rgb(94, 110, 126)',
  textTransform: 'none',
  borderRadius: '30px',
  padding: '6px 12px',
  minWidth: '60px',
  fontSize: '12px',
  fontWeight: '600',
  border: active ? `1px solid rgb(30, 111, 217)` : `1px solid rgb(228, 234, 241)`,
  transition: 'all 0.2s ease',
  fontFamily: FORD_TYPOGRAPHY.newfont,
  '&:hover': {
    backgroundColor: active ? FORD_COLORS.backgroundford : COMMON_COLORS.hoverBackground,
    borderColor: COMMON_COLORS.primary,
    color: COMMON_COLORS.primary,
  },
  '&:disabled': {
    backgroundColor: 'transparent',
     color: 'rgb(94, 110, 126)',
    // borderColor: COMMON_COLORS.border,
    opacity: 0.5,
  },
}));
